import jsPDF from "jspdf";
import "jspdf-autotable";
import "../../../../assets/font/THSarabunNew-normal";
import "../../../../assets/font/THSarabunNew-bold";
import dayjs from "dayjs";

// ─── helpers ────────────────────────────────────────────────
const dash = (v) =>
  v === null || v === undefined || v === "" ? "-" : String(v);

const formatMoney = (v) => {
  if (v === null || v === undefined || v === "") return "-";
  return `${Number(v).toLocaleString("th-TH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} บาท`;
};

const formatDate = (v) => (v ? dayjs(v).format("DD/MM/YYYY") : "-");

const formatDateTime = (v) =>
  v
    ? `${dayjs(v).format("DD/MM/YYYY")} เวลา ${dayjs(v).format("HH:mm")} น.`
    : "-";

const expenseStatusText = (r) => {
  if (r.FLAG === "C") return "ยกเลิก";
  if (r.PAYDT) return "เคลียร์แล้ว";
  return "รอเคลียร์";
};

const assetStatusText = (assetInfoList = []) => {
  if (!assetInfoList.length) return "ยังไม่ได้สืบทรัพย์ออนไลน์";
  return assetInfoList
    .map(
      (info) =>
        `${info.relation ? `${info.relation}: ` : ""}${info.hasAsset ? "มีทรัพย์" : "ไม่มีทรัพย์"}`,
    )
    .join(", ");
};

// ─── main export ────────────────────────────────────────────
// ใบปะหน้าคดี: ใช้ข้อมูลชุดเดียวกับที่แสดงบนหน้าจอ JudgeDocument
export const exportJudgeCoverSheetPdf = ({
  contno,
  lawsuit,
  plaintiffCompanyName,
  judge,
  flagLoanNew,
  defendantList = [],
  expenseList = [],
  noteList = [],
  totalNetpay = 0,
  totalPay = 0,
}) => {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const setF = (style = "normal", size = 12) => {
    doc.setFont("THSarabunNew", style);
    doc.setFontSize(size);
  };
  const black = () => doc.setTextColor(0, 0, 0);

  const pageW = 210;
  const pageH = 297;
  const mL = 14;
  const mR = 14;
  const xR = pageW - mR;
  const contentW = xR - mL;

  let y = 15;

  const ensureSpace = (needed) => {
    if (y + needed > pageH - 12) {
      doc.addPage();
      y = 15;
    }
  };

  const sectionTitle = (text) => {
    ensureSpace(10);
    setF("bold", 14);
    doc.setTextColor(0, 51, 102);
    doc.text(text, mL, y);
    doc.setDrawColor(0, 51, 102);
    doc.setLineWidth(0.4);
    doc.line(mL, y + 1.5, xR, y + 1.5);
    black();
    y += 7;
  };

  const fieldRow = (fields, ratios) => {
    ensureSpace(6);
    setF("normal", 12);
    black();
    let x = mL;
    const widths = ratios.map((r) => contentW * r);
    fields.forEach((f, i) => {
      const txt = `${f.label} : ${dash(f.value)}`;
      const lines = doc.splitTextToSize(txt, widths[i] - 3);
      doc.text(lines, x, y);
      x += widths[i];
    });
    y += 6;
  };

  // ── หัวเอกสาร ──
  setF("normal", 11);
  doc.text(`วันที่พิมพ์ : ${dayjs().format("DD/MM/YYYY HH:mm:ss")}`, mL, y);
  setF("bold", 17);
  doc.text("ใบปะหน้าพิพากษา", pageW / 2, y, { align: "center" });
  setF("normal", 12);
  doc.text(`เลขที่สัญญา : ${dash(contno)}`, xR, y, { align: "right" });
  y += 6;
  doc.text(
    `ผู้รับผิดชอบ : ${lawsuit?.FNAME ? `${lawsuit.NNAME || "-"}` : "-"}`,
    xR,
    y,
    { align: "right" },
  );
  y += 8;

  // ── ข้อมูลคำฟ้อง ──
  sectionTitle("ข้อมูลคำฟ้อง");
  fieldRow(
    [
      { label: "เลขคดีดำ", value: lawsuit?.blackCaseNumber },
      { label: "ศาลที่ยื่นฟ้อง", value: lawsuit?.provincialCourt },
    ],
    [0.5, 0.5],
  );
  fieldRow(
    [
      { label: "วันที่ยื่นฟ้อง", value: formatDate(lawsuit?.dateOfPlaint) },
      { label: "หัวข้อคดี", value: lawsuit?.subject },
    ],
    [0.5, 0.5],
  );
  fieldRow(
    [
      {
        label: "วันนัดพิจารณาคดี",
        value: formatDateTime(lawsuit?.considerationDate),
      },
      { label: "ทุนทรัพย์ฟ้อง", value: formatMoney(lawsuit?.litigationFunds) },
    ],
    [0.5, 0.5],
  );
  fieldRow(
    [
      { label: "ค่าขาดประโยชน์", value: formatMoney(lawsuit?.lackOfBenefits) },
      { label: "โจทย์", value: plaintiffCompanyName || lawsuit?.COMPANY_ID },
    ],
    [0.5, 0.5],
  );

  y += 3;

  // ── ข้อมูลคำพิพากษา ──
  sectionTitle(`ข้อมูลคำพิพากษา (${flagLoanNew === 1 ? "ทำยอม" : "พิพากษา"})`);
  fieldRow(
    [
      { label: "เลขคดีแดง", value: judge?.redCaseNumber },
      { label: "วันที่มีคำพิพากษา", value: formatDate(judge?.judgeDate) },
    ],
    [0.5, 0.5],
  );
  fieldRow(
    [
      {
        label: "วันที่วางคำบังคับ",
        value: formatDate(judge?.enforceCaseDate),
      },
      { label: "ค่าแทน", value: formatMoney(judge?.judgement) },
    ],
    [0.5, 0.5],
  );
  fieldRow(
    [
      { label: "ต้นเงิน", value: formatMoney(judge?.judgementLack) },
      {
        label: "อัตราดอกเบี้ย",
        value: `${judge?.judgementLack !== "0" ? judge?.interestRate || 0 : judge?.judgeInterestRate || 0} %`,
      },
    ],
    [0.5, 0.5],
  );
  fieldRow(
    [
      {
        label: "ค่าธรรมเนียมศาล / ค่าติดตาม / ค่าทนายความ",
        value: `${formatMoney(judge?.fee)} / ${formatMoney(judge?.trackingFee)} / ${formatMoney(judge?.attorneyFees)}`,
      },
    ],
    [1],
  );
  ensureSpace(7);
  setF("bold", 13);
  doc.setTextColor(178, 34, 34);
  doc.text(`รวมยอดหนี้ตามคำพิพากษา : ${formatMoney(judge?.totalJugde)}`, mL, y);
  black();
  y += 8;

  // ── รายชื่อจำเลย ──
  sectionTitle(`รายชื่อจำเลย (${defendantList.length} คน)`);
  if (defendantList.length) {
    doc.autoTable({
      head: [["ลำดับ", "ชื่อ-นามสกุล", "คำพิพากษาที่", "สถานะสืบทรัพย์"]],
      body: defendantList.map((d) => [
        d.defendantNumber,
        `${dash(d.SNAM)}${dash(d.NAME1)} ${dash(d.NAME2)}`
          .replace(/\s+/g, " ")
          .trim(),
        d.judgeNumber === 1 ? "1" : "คำพิพากษาถัดไป",
        assetStatusText(d.assetInfoList),
      ]),
      startY: y,
      margin: { left: mL, right: mR },
      theme: "striped",
      styles: { font: "THSarabunNew", fontSize: 12, textColor: [0, 0, 0] },
      headStyles: { fillColor: [200, 225, 245], textColor: [0, 0, 0] },
    });
    y = doc.lastAutoTable.finalY + 8;
  } else {
    setF("normal", 12);
    doc.text("ไม่มีรายชื่อจำเลย", mL, y);
    y += 8;
  }

  // ── ข้อมูลค่าใช้จ่าย ──
  sectionTitle("ข้อมูลค่าใช้จ่าย");
  if (expenseList.length) {
    doc.autoTable({
      head: [["รายการค่าใช้จ่าย", "ยอดเบิก", "วันที่เบิก", "ยอดชำระจริง"]],
      body: expenseList.map((r) => [
        r.description || "-",
        formatMoney(r.NETPAY),
        formatDate(r.NETPAY_DT),
        r.PAY !== null ? formatMoney(r.PAY) : "-",
        formatDate(r.PAYDT),
        expenseStatusText(r),
      ]),
      foot: [
        [
          "รวมทั้งหมด",
          formatMoney(totalNetpay),
          "",
          formatMoney(totalPay),
          "",
          "",
        ],
      ],
      startY: y,
      margin: { left: mL, right: mR },
      theme: "striped",
      styles: { font: "THSarabunNew", fontSize: 12, textColor: [0, 0, 0] },
      headStyles: { fillColor: [200, 225, 245], textColor: [0, 0, 0] },
      footStyles: {
        font: "THSarabunNew",
        fontStyle: "bold",
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
      },
    });
    y = doc.lastAutoTable.finalY + 8;
  } else {
    setF("normal", 12);
    doc.text("ไม่มีรายการค่าใช้จ่าย", mL, y);
    y += 8;
  }

  // ── หมายเหตุ ──
  sectionTitle("หมายเหตุ");
  if (noteList.length) {
    doc.autoTable({
      head: [["ผู้บันทึก", "วันที่", "หมายเหตุ"]],
      body: noteList.map((n) => [
        n.USERNAME || "-",
        formatDate(n.CreatedAt),
        n.note || "-",
      ]),
      startY: y,
      margin: { left: mL, right: mR },
      theme: "striped",
      styles: { font: "THSarabunNew", fontSize: 12, textColor: [0, 0, 0] },
      headStyles: { fillColor: [0, 51, 102], textColor: [255, 255, 255] },
    });
    y = doc.lastAutoTable.finalY + 4;
  } else {
    setF("normal", 12);
    doc.text("ไม่มีหมายเหตุ", mL, y);
    y += 4;
  }

  // ── เปิด / สั่งพิมพ์ ──
  const blob = doc.output("blob");
  const url = URL.createObjectURL(blob);
  const win = window.open(url);
  if (win) win.onload = () => win.print();
  else alert("กรุณาปิดการบล็อกป๊อปอัปเพื่อใช้งานฟังก์ชันนี้");
};

export default exportJudgeCoverSheetPdf;
